/**
 * VendorDetailPage — chi tiết Vendor.
 * Header: vendor info từ useGetVendorQuery.
 * Tabs: General Info / Banks / Licenses.
 *
 * Banks tab: list cp_bank cột (bank, bank_account, account_name, branch, note).
 *            Buttons: + New, Edit, Delete.
 * Licenses tab: list cp_license cột (license_cert_no, valid_from, valid_to,
 *               is_valid chip, si, sd, note). Buttons: + New, Toggle Valid,
 *               Edit, Delete.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import type { GridColDef } from '@mui/x-data-grid'
import { useNavigate, useParams } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useListBanksQuery } from '@/store/api/banksApi'
import { useGetVendorQuery } from '@/store/api/vendorsApi'
import {
  useCreateVendorBankMutation,
  useDeleteVendorBankMutation,
  useListVendorBanksQuery,
  useUpdateVendorBankMutation,
} from '@/store/api/vendorBanksApi'
import {
  useCreateVendorLicenseMutation,
  useDeleteVendorLicenseMutation,
  useListVendorLicensesQuery,
  useUpdateVendorLicenseMutation,
} from '@/store/api/vendorLicensesApi'
import type {
  CpBankOut,
  Vendor,
  VendorLicenseOut,
} from '@/types/api'
import { formatDate } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'

import VendorFormDialog from './VendorFormDialog'

// ---------- General Info tab ----------
function GeneralInfoTab({ v }: { v: Vendor }) {
  const FieldRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box display="flex" gap={2} sx={{ flex: '1 1 280px', minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </Typography>
    </Box>
  )
  const typeLabel = v.type === 0 ? 'Customer' : v.type === 1 ? 'Vendor' : v.type === 2 ? 'Both' : ''
  return (
    <Box display="flex" flexDirection="column" gap={3} pt={2}>
      <Box display="flex" flexWrap="wrap" gap={2}>
        <FieldRow label="Code" value={v.code} />
        <FieldRow label="Name" value={v.name} />
        <FieldRow label="Short Name" value={v.short_name} />
        <FieldRow label="Type" value={typeLabel} />
        <FieldRow label="VAT Code" value={v.vat_code} />
        <FieldRow label="Phone" value={v.phone} />
        <FieldRow label="Mobile" value={v.mobile} />
        <FieldRow label="Email" value={v.email} />
        <FieldRow label="Address" value={v.address} />
        <FieldRow label="Note" value={v.note} />
      </Box>
    </Box>
  )
}

// ---------- Banks tab ----------
interface BankFormValues {
  bank: string
  bank_account: string
  account_name: string
  branch: string
  note: string
}
const EMPTY_BANK: BankFormValues = {
  bank: '',
  bank_account: '',
  account_name: '',
  branch: '',
  note: '',
}

function BanksTab({ code }: { code: string }) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const { data, isLoading, error, isFetching } = useListVendorBanksQuery({ code })
  const { data: bankList } = useListBanksQuery({ page: 1, page_size: 1_000_000 })
  const banks = bankList?.items ?? []
  const items = data?.data ?? []

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<CpBankOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<CpBankOut | null>(null)
  const [form, setForm] = useState<BankFormValues>(EMPTY_BANK)

  const [createBank, { isLoading: creating }] = useCreateVendorBankMutation()
  const [updateBank, { isLoading: updating }] = useUpdateVendorBankMutation()
  const [deleteBank, { isLoading: deleting }] = useDeleteVendorBankMutation()
  const formLoading = creating || updating

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      setForm({
        bank: editing.bank ?? '',
        bank_account: editing.bank_account ?? '',
        account_name: editing.account_name ?? '',
        branch: editing.branch ?? '',
        note: editing.note ?? '',
      })
    } else {
      setForm(EMPTY_BANK)
    }
  }, [dialogOpen, isEdit, editing])

  const rows = useMemo(
    () => items.map((b, i) => ({ id: `${b.bank}-${b.bank_account}-${i}`, ...b })),
    [items],
  )

  const columns = useMemo<GridColDef<CpBankOut & { id: string }>[]>(
    () => [
      { field: 'bank', headerName: 'Bank', width: 120 },
      { field: 'bank_name', headerName: 'Bank Name', flex: 1, minWidth: 200 },
      { field: 'bank_account', headerName: 'Account No.', width: 180 },
      { field: 'account_name', headerName: 'Account Holder', flex: 1, minWidth: 200 },
      { field: 'branch', headerName: 'Branch', flex: 1, minWidth: 160 },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 160 },
      {
        field: '_actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" disabled={!canEdit} onClick={() => setEditing(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              disabled={!canEdit}
              onClick={() => setConfirmDel(p.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => {
    setCreateOpen(false)
    setEditing(null)
  }

  const handleSubmit = async () => {
    const payload = {
      bank: form.bank.trim(),
      bank_account: form.bank_account.trim(),
      account_name: form.account_name.trim() || null,
      branch: form.branch.trim() || null,
      note: form.note.trim() || null,
    }
    if (!payload.bank || !payload.bank_account) {
      snack.error('Bank and bank account are required')
      return
    }
    try {
      if (isEdit && editing) {
        await updateBank({
          code,
          bank: editing.bank,
          bank_account: editing.bank_account,
          body: {
            bank: payload.bank,
            branch: payload.branch,
            account_name: payload.account_name,
            note: payload.note,
          },
        }).unwrap()
        snack.success('Bank updated')
      } else {
        await createBank({ code, body: payload }).unwrap()
        snack.success('Bank added')
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save bank'))
    }
  }

  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try {
      await deleteBank({
        code,
        bank: confirmDel.bank,
        bank_account: confirmDel.bank_account,
      }).unwrap()
      snack.success(`Deleted account ${confirmDel.bank_account}`)
      setConfirmDel(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete bank'))
    }
  }

  const isEmpty = !isLoading && !error && rows.length === 0

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canEdit}
          onClick={() => setCreateOpen(true)}
        >
          New Bank
        </Button>
      </Box>
      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <DataTableMUI rows={rows} columns={columns} loading={isFetching} height={420} />
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit bank account' : 'New bank account'}
        loading={formLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void handleSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Bank *"
            select
            size="small"
            fullWidth
            value={form.bank}
            onChange={(e) => setForm({ ...form, bank: e.target.value })}
          >
            <MenuItem value="">— Select —</MenuItem>
            {banks.map((b) => (
              <MenuItem key={b.bank_code} value={b.bank_code}>
                {b.bank_code} — {b.bank_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Account No. *"
            size="small"
            fullWidth
            value={form.bank_account}
            onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
            disabled={isEdit}
          />
          <TextField
            label="Account Holder"
            size="small"
            fullWidth
            value={form.account_name}
            onChange={(e) => setForm({ ...form, account_name: e.target.value })}
          />
          <TextField
            label="Branch"
            size="small"
            fullWidth
            value={form.branch}
            onChange={(e) => setForm({ ...form, branch: e.target.value })}
          />
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete bank account"
        message={
          confirmDel
            ? `Delete account ${confirmDel.bank} / ${confirmDel.bank_account}?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}

// ---------- Licenses tab ----------
interface LicenseFormValues {
  license_cert_no: string
  valid_from: string
  valid_to: string
  is_valid: boolean
  si: string
  sd: string
  note: string
}
const EMPTY_LICENSE: LicenseFormValues = {
  license_cert_no: '',
  valid_from: '',
  valid_to: '',
  is_valid: true,
  si: '',
  sd: '',
  note: '',
}

function LicensesTab({ code }: { code: string }) {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const { data, isLoading, error, isFetching } = useListVendorLicensesQuery({ code })
  const items = data?.data ?? []

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<VendorLicenseOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<VendorLicenseOut | null>(null)
  const [form, setForm] = useState<LicenseFormValues>(EMPTY_LICENSE)

  const [createLicense, { isLoading: creating }] = useCreateVendorLicenseMutation()
  const [updateLicense, { isLoading: updating }] = useUpdateVendorLicenseMutation()
  const [deleteLicense, { isLoading: deleting }] = useDeleteVendorLicenseMutation()
  const formLoading = creating || updating

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      setForm({
        license_cert_no: editing.license_cert_no ?? '',
        valid_from: editing.valid_from ?? '',
        valid_to: editing.valid_to ?? '',
        is_valid: !!editing.is_valid,
        si: editing.si ?? '',
        sd: editing.sd ?? '',
        note: editing.note ?? '',
      })
    } else {
      setForm(EMPTY_LICENSE)
    }
  }, [dialogOpen, isEdit, editing])

  const rows = useMemo(
    () => items.map((l) => ({ id: l.license_id, ...l })),
    [items],
  )

  const handleToggleValid = async (l: VendorLicenseOut) => {
    try {
      await updateLicense({
        code,
        license_id: l.license_id,
        body: { is_valid: !l.is_valid },
      }).unwrap()
      snack.success(`${!l.is_valid ? 'Activated' : 'Deactivated'} license`)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to update status'))
    }
  }

  const columns = useMemo<GridColDef<VendorLicenseOut & { id: number }>[]>(
    () => [
      { field: 'license_cert_no', headerName: 'License Cert No', width: 180 },
      {
        field: 'valid_from',
        headerName: 'Valid From',
        width: 110,
        valueGetter: (_v, row) => formatDate(row.valid_from),
      },
      {
        field: 'valid_to',
        headerName: 'Valid To',
        width: 110,
        valueGetter: (_v, row) => formatDate(row.valid_to),
      },
      {
        field: 'is_valid',
        headerName: 'Active',
        width: 130,
        renderCell: (p) =>
          p.row.is_valid ? (
            <Chip size="small" label="Valid" color="success" />
          ) : (
            <Chip size="small" label="Invalid" color="default" />
          ),
      },
      { field: 'si', headerName: 'SI', width: 80 },
      { field: 'sd', headerName: 'SD', width: 80 },
      { field: 'note', headerName: 'Note', flex: 1, minWidth: 180 },
      {
        field: '_actions',
        headerName: '',
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (p) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={p.row.is_valid ? 'Toggle Invalid' : 'Toggle Valid'}>
              <Switch
                size="small"
                checked={!!p.row.is_valid}
                disabled={!canEdit}
                onChange={() => void handleToggleValid(p.row)}
              />
            </Tooltip>
            <IconButton size="small" disabled={!canEdit} onClick={() => setEditing(p.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              disabled={!canEdit}
              onClick={() => setConfirmDel(p.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleCloseDialog = () => {
    setCreateOpen(false)
    setEditing(null)
  }

  const handleSubmit = async () => {
    const payload = {
      license_cert_no: form.license_cert_no.trim(),
      valid_from: form.valid_from || null,
      valid_to: form.valid_to || null,
      is_valid: form.is_valid,
      si: form.si.trim() || null,
      sd: form.sd.trim() || null,
      note: form.note.trim() || null,
    }
    if (!payload.license_cert_no) {
      snack.error('License Cert No is required')
      return
    }
    try {
      if (isEdit && editing) {
        await updateLicense({
          code,
          license_id: editing.license_id,
          body: payload,
        }).unwrap()
        snack.success('License updated')
      } else {
        await createLicense({ code, body: payload }).unwrap()
        snack.success('License added')
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save license'))
    }
  }

  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try {
      await deleteLicense({ code, license_id: confirmDel.license_id }).unwrap()
      snack.success(`Deleted license ${confirmDel.license_cert_no}`)
      setConfirmDel(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to delete license'))
    }
  }

  const isEmpty = !isLoading && !error && rows.length === 0

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          disabled={!canEdit}
          onClick={() => setCreateOpen(true)}
        >
          New License
        </Button>
      </Box>
      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <DataTableMUI rows={rows} columns={columns} loading={isFetching} height={420} />
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit License' : 'New License'}
        loading={formLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void handleSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="License Cert No *"
            size="small"
            fullWidth
            value={form.license_cert_no}
            onChange={(e) => setForm({ ...form, license_cert_no: e.target.value })}
          />
          <Box display="flex" gap={2} flexWrap="wrap">
            <DatePicker
              label="Valid From"
              format="DD-MMM-YYYY"
              value={form.valid_from ? dayjs(form.valid_from) : null}
              onChange={(d) =>
                setForm({ ...form, valid_from: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
              }
              slotProps={{ textField: { size: 'small', sx: { flex: '1 1 200px' } } }}
            />
            <DatePicker
              label="Valid To"
              format="DD-MMM-YYYY"
              value={form.valid_to ? dayjs(form.valid_to) : null}
              onChange={(d) =>
                setForm({ ...form, valid_to: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
              }
              slotProps={{ textField: { size: 'small', sx: { flex: '1 1 200px' } } }}
            />
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="SI"
              size="small"
              value={form.si}
              onChange={(e) => setForm({ ...form, si: e.target.value })}
              sx={{ flex: '1 1 140px' }}
            />
            <TextField
              label="SD"
              size="small"
              value={form.sd}
              onChange={(e) => setForm({ ...form, sd: e.target.value })}
              sx={{ flex: '1 1 140px' }}
            />
            <Box display="flex" alignItems="center" gap={1} sx={{ flex: '1 1 200px' }}>
              <Switch
                checked={form.is_valid}
                onChange={(e) => setForm({ ...form, is_valid: e.target.checked })}
              />
              <Typography variant="body2">Active</Typography>
            </Box>
          </Box>
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete license"
        message={confirmDel ? `Delete license ${confirmDel.license_cert_no}?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}

// ---------- Main page ----------
const TAB_LABELS = ['General Info', 'Banks', 'Licenses']

export function VendorDetailPage() {
  const { canEdit } = usePagePermission()
  const { code = '' } = useParams<{ code: string }>()
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetVendorQuery(code, { skip: !code })
  const vendor = data?.data
  const [tab, setTab] = useState(0)
  const [editOpen, setEditOpen] = useState(false)

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Tooltip title="Back">
          <IconButton onClick={() => navigate('/vendors')}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h5" fontWeight={700}>
          {vendor?.code ?? code}
        </Typography>
        {vendor?.name ? (
          <Typography variant="subtitle1" color="text.secondary">
            — {vendor.name}
          </Typography>
        ) : null}
        <Box flex={1} />
        {vendor && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            disabled={!canEdit}
            onClick={() => setEditOpen(true)}
          >
            Edit Vendor
          </Button>
        )}
      </Box>

      <Divider />

      <PageDataState loading={isLoading} error={error} empty={!isLoading && !vendor}>
        {vendor ? (
          <Paper sx={{ p: 2 }}>
            <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
              {TAB_LABELS.map((l) => (
                <Tab key={l} label={l} />
              ))}
            </Tabs>
            <Box>
              {tab === 0 && <GeneralInfoTab v={vendor} />}
              {tab === 1 && <BanksTab code={vendor.code} />}
              {tab === 2 && <LicensesTab code={vendor.code} />}
            </Box>
          </Paper>
        ) : null}
      </PageDataState>

      <VendorFormDialog
        open={editOpen}
        mode="edit"
        initialValue={vendor ?? null}
        onClose={() => setEditOpen(false)}
      />
    </Box>
  )
}

export default VendorDetailPage
